import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  isDespiaNative, 
  triggerHaptic, 
  storeCredentials, 
  getStoredCredentials, 
  clearStoredCredentials,
  safeDespia,
} from '@/utils/despia';

declare global {
  interface Window {
    bioauthSuccess?: () => void;
    bioauthFailure?: () => void;
    bioauthUnavailable?: () => void;
  }
}

interface SecurityPreferences {
  biometric_enabled: boolean;
}

interface UseBiometricAuthReturn {
  isNativeApp: boolean;
  isBiometricAvailable: boolean;
  isBiometricEnabled: boolean;
  isAuthenticating: boolean;
  triggerBiometricAuth: () => Promise<boolean>;
  enableBiometricAuth: (email: string, password: string) => Promise<boolean>;
  disableBiometricAuth: () => Promise<void>;
  loginWithBiometrics: () => Promise<{ success: boolean; email?: string; password?: string }>;
  checkBiometricStatus: () => Promise<void>;
  cancelBiometricAuth: () => void;
}

export function useBiometricAuth(userId?: string): UseBiometricAuthReturn {
  const [isNativeApp, setIsNativeApp] = useState(false);
  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
  const [isBiometricEnabled, setIsBiometricEnabled] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  // Use ref instead of state to avoid stale closure issues
  const authResolveRef = useRef<((success: boolean) => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if running in Despia native environment
  useEffect(() => {
    const isDespia = isDespiaNative();
    setIsNativeApp(isDespia);
    
    // Assume biometrics are available on native devices
    // The actual check happens when we try to use it
    setIsBiometricAvailable(isDespia);
  }, []);

  // Initialize biometric callbacks - no dependencies on authResolve since we use ref
  useEffect(() => {
    if (!isNativeApp) return;

    window.bioauthSuccess = () => {
      console.log('[BiometricAuth] bioauthSuccess callback fired, ref exists:', !!authResolveRef.current);
      setIsAuthenticating(false);
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Trigger haptic feedback
      triggerHaptic('success');
      
      if (authResolveRef.current) {
        console.log('[BiometricAuth] Resolving with true');
        authResolveRef.current(true);
        authResolveRef.current = null;
      } else {
        console.warn('[BiometricAuth] No resolver found on success');
      }
    };

    window.bioauthFailure = () => {
      console.log('[BiometricAuth] bioauthFailure callback fired, ref exists:', !!authResolveRef.current);
      setIsAuthenticating(false);
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      triggerHaptic('error');
      toast.error('Biometric authentication failed');
      
      if (authResolveRef.current) {
        console.log('[BiometricAuth] Resolving with false');
        authResolveRef.current(false);
        authResolveRef.current = null;
      } else {
        console.warn('[BiometricAuth] No resolver found on failure');
      }
    };

    window.bioauthUnavailable = () => {
      console.log('[BiometricAuth] bioauthUnavailable callback fired, ref exists:', !!authResolveRef.current);
      setIsAuthenticating(false);
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setIsBiometricAvailable(false);
      toast.error('Biometric authentication is not available on this device');
      
      if (authResolveRef.current) {
        console.log('[BiometricAuth] Resolving with false (unavailable)');
        authResolveRef.current(false);
        authResolveRef.current = null;
      } else {
        console.warn('[BiometricAuth] No resolver found on unavailable');
      }
    };

    return () => {
      window.bioauthSuccess = undefined;
      window.bioauthFailure = undefined;
      window.bioauthUnavailable = undefined;
    };
  }, [isNativeApp]); // Only depends on isNativeApp now

  // Check biometric status from profile
  const checkBiometricStatus = useCallback(async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('security_preferences')
        .eq('id', userId)
        .single();

      if (error) throw error;

      const prefs = data?.security_preferences as unknown as SecurityPreferences | null;
      setIsBiometricEnabled(prefs?.biometric_enabled ?? false);
    } catch (error) {
      console.error('[BiometricAuth] Error checking biometric status:', error);
    }
  }, [userId]);

  useEffect(() => {
    checkBiometricStatus();
  }, [checkBiometricStatus]);

  // Cancel biometric authentication manually
  const cancelBiometricAuth = useCallback(() => {
    console.log('[BiometricAuth] Manual cancel triggered');
    setIsAuthenticating(false);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (authResolveRef.current) {
      authResolveRef.current(false);
      authResolveRef.current = null;
    }
  }, []);

  // Trigger biometric authentication
  const triggerBiometricAuth = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!isNativeApp || !isBiometricAvailable) {
        console.log('[BiometricAuth] Not triggering - native:', isNativeApp, 'available:', isBiometricAvailable);
        resolve(false);
        return;
      }

      setIsAuthenticating(true);
      
      // Store the resolver in ref (always current, no stale closure)
      authResolveRef.current = resolve;
      console.log('[BiometricAuth] Resolver set in ref');
      
      // Set timeout to prevent getting stuck if native callback never fires
      timeoutRef.current = setTimeout(() => {
        console.log('[BiometricAuth] Timeout - no callback received after 8s');
        setIsAuthenticating(false);
        if (authResolveRef.current) {
          authResolveRef.current(false);
          authResolveRef.current = null;
        }
        toast.error('Face ID timed out. Please try again.');
      }, 8000); // 8 second timeout (reduced from 15s)

      // Call Despia biometric auth using the SDK
      console.log('[BiometricAuth] Triggering bioauth://');
      safeDespia('bioauth://');
    });
  }, [isNativeApp, isBiometricAvailable]);

  // Enable biometric auth for this user with credential storage
  const enableBiometricAuth = useCallback(async (email: string, password: string): Promise<boolean> => {
    if (!userId) return false;

    // First verify with biometrics
    const authSuccess = await triggerBiometricAuth();
    if (!authSuccess) return false;

    try {
      // Store credentials securely in keychain
      const credentialsSaved = await storeCredentials(email, password);
      if (!credentialsSaved) {
        toast.error('Failed to store credentials securely');
        return false;
      }

      // Update profile to mark biometric as enabled
      const { error } = await supabase
        .from('profiles')
        .update({
          security_preferences: { biometric_enabled: true }
        })
        .eq('id', userId);

      if (error) throw error;

      setIsBiometricEnabled(true);
      triggerHaptic('success');
      toast.success('Face ID / Touch ID enabled');
      return true;
    } catch (error) {
      console.error('[BiometricAuth] Error enabling biometric:', error);
      toast.error('Failed to enable biometric authentication');
      return false;
    }
  }, [userId, triggerBiometricAuth]);

  // Login with biometrics - returns credentials after verification
  const loginWithBiometrics = useCallback(async (): Promise<{ success: boolean; email?: string; password?: string }> => {
    // First verify with biometrics
    const authSuccess = await triggerBiometricAuth();
    if (!authSuccess) {
      return { success: false };
    }

    // Get stored credentials
    const credentials = await getStoredCredentials();
    if (!credentials) {
      toast.error('No saved credentials found. Please sign in with your password.');
      return { success: false };
    }

    return { 
      success: true, 
      email: credentials.email, 
      password: credentials.password 
    };
  }, [triggerBiometricAuth]);

  // Disable biometric auth
  const disableBiometricAuth = useCallback(async () => {
    if (!userId) return;

    try {
      // Clear stored credentials
      await clearStoredCredentials();

      const { error } = await supabase
        .from('profiles')
        .update({
          security_preferences: { biometric_enabled: false }
        })
        .eq('id', userId);

      if (error) throw error;

      setIsBiometricEnabled(false);
      triggerHaptic('success');
      toast.success('Face ID / Touch ID disabled');
    } catch (error) {
      console.error('[BiometricAuth] Error disabling biometric:', error);
      toast.error('Failed to disable biometric authentication');
    }
  }, [userId]);

  return {
    isNativeApp,
    isBiometricAvailable,
    isBiometricEnabled,
    isAuthenticating,
    triggerBiometricAuth,
    enableBiometricAuth,
    disableBiometricAuth,
    loginWithBiometrics,
    checkBiometricStatus,
    cancelBiometricAuth,
  };
}

// Re-export from despia utils for convenience
export { isDespiaNative } from '@/utils/despia';

// Initialize biometric SDK script (call once at app startup)
export function initBiometricCallbacks() {
  // Callbacks are set up by the hook when it mounts
  console.log('[BiometricAuth] Biometric callbacks initialized');
}
